<template>
    <div class="weeduc-admin-page corporate-page corporate-preferences-page text-start p-0">
        <header class="weeduc-admin-page__header">
            <div class="weeduc-admin-page__header-lead">
                <TitlePage
                    :title="t.preferences.title"
                    :subtitle="t.preferences.subtitle"
                    :hasMargin="false"
                />
            </div>
        </header>

        <div class="weeduc-admin-page__shell">
            <div v-if="!canEdit" class="corporate-preferences-banner">
                {{ t.preferences.readOnly }}
            </div>

            <div class="weeduc-admin-page__panel">
                <div v-if="loading" class="corporate-placeholder">
                    <Loader :isActive="false" />
                </div>
                <div v-else-if="error" class="corporate-placeholder">{{ t.common.error }}</div>

                <template v-else>
                    <nav class="weeduc-admin-tabs" :aria-label="t.preferences.tabsAriaLabel">
                        <button
                            v-for="tab in tabs"
                            :key="tab.id"
                            type="button"
                            class="weeduc-admin-tab"
                            :class="{ 'weeduc-admin-tab--active': activeTab === tab.id }"
                            @click="activeTab = tab.id"
                        >
                            {{ tab.label }}
                        </button>
                    </nav>

                    <!-- Site público -->
                    <section v-show="activeTab === 'site'" class="corporate-preferences-panel">
                        <p class="corporate-section-label">{{ t.preferences.sectionVisibility }}</p>
                        <p class="corporate-preferences-lead">{{ t.preferences.portalLead }}</p>

                        <div class="corporate-preferences-list">
                            <div class="corporate-preferences-row">
                                <div>
                                    <strong>{{ t.preferences.siteEnabled }}</strong>
                                    <p>{{ t.preferences.siteEnabledHint }}</p>
                                </div>
                                <v-switch
                                    :model-value="form.siteEnabled"
                                    color="primary"
                                    hide-details
                                    inset
                                    :disabled="!canEdit || saving"
                                    :loading="savingKey === 'siteEnabled'"
                                    @update:model-value="(val) => onToggle('siteEnabled', val)"
                                />
                            </div>
                        </div>

                        <template v-if="form.siteEnabled">
                            <p class="corporate-section-label">{{ t.preferences.sectionUrls }}</p>
                            <p class="corporate-preferences-lead">{{ t.preferences.urlsLead }}</p>

                            <div class="corporate-preferences-urls">
                                <article
                                    v-for="item in siteUrlItems"
                                    :key="item.key"
                                    class="corporate-preferences-url-card"
                                    :class="{
                                        'corporate-preferences-url-card--main': item.key === 'url',
                                        'is-saving': savingKey === item.key,
                                    }"
                                >
                                    <div class="corporate-preferences-url-card__copy">
                                        <strong>{{ item.title }}</strong>
                                        <p>{{ item.hint }}</p>
                                    </div>
                                    <div class="corporate-preferences-url-card__control">
                                        <input
                                            :id="`corporate-url-${item.key}`"
                                            v-model="form[item.key]"
                                            type="text"
                                            class="corporate-preferences-url-card__input"
                                            :placeholder="t.preferences.urlPlaceholder"
                                            :disabled="!canEdit || saving"
                                            :aria-label="item.title"
                                            @blur="onUrlBlur(item.key)"
                                        />
                                        <div class="corporate-preferences-url-card__actions">
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(form[item.key])"
                                                :aria-label="t.preferences.openUrl"
                                                @mousedown.prevent
                                                @click="openUrl(form[item.key])"
                                            >
                                                <v-icon size="18">mdi-open-in-new</v-icon>
                                            </button>
                                            <button
                                                type="button"
                                                class="corporate-preferences-url-action"
                                                :disabled="!canOpenUrl(form[item.key])"
                                                :aria-label="t.preferences.copyUrl"
                                                @mousedown.prevent
                                                @click="copyUrl(form[item.key])"
                                            >
                                                <v-icon size="18">mdi-content-copy</v-icon>
                                            </button>
                                        </div>
                                    </div>
                                </article>
                            </div>
                        </template>
                    </section>

                    <!-- Portal do colaborador -->
                    <section
                        v-show="activeTab === 'collaborator'"
                        class="corporate-preferences-panel"
                    >
                        <p class="corporate-section-label">{{ t.preferences.sectionModules }}</p>
                        <p class="corporate-preferences-lead">{{ t.preferences.collaboratorLead }}</p>

                        <div class="corporate-preferences-list">
                            <div
                                v-for="item in collaboratorToggleItems"
                                :key="item.key"
                                class="corporate-preferences-row"
                            >
                                <div>
                                    <strong>{{ item.title }}</strong>
                                    <p>{{ item.hint }}</p>
                                </div>
                                <v-switch
                                    :model-value="form[item.key]"
                                    color="primary"
                                    hide-details
                                    inset
                                    :disabled="!canEdit || saving"
                                    :loading="savingKey === item.key"
                                    @update:model-value="(val) => onToggle(item.key, val)"
                                />
                            </div>
                        </div>
                    </section>

                    <!-- Layout do portal -->
                    <section
                        v-show="activeTab === 'banner'"
                        class="corporate-preferences-panel"
                    >
                        <p class="corporate-section-label">{{ t.preferences.logoLabel }}</p>
                        <p class="corporate-preferences-lead">{{ t.preferences.logoHint }}</p>
                        <div class="corporate-preferences-upload">
                            <CustomFileInput
                                v-model="logoValue"
                                height="120"
                                :can-delete="false"
                                :info-text="t.preferences.uploadClick"
                                @update:modelValue="onLogoChange"
                            />
                        </div>
                    </section>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { toast } from 'vue3-toastify';
import TitlePage from '@/components/Weflix/titlePage.vue';
import Loader from '@/components/Loader/loader.vue';
import CustomFileInput from '@/components/Form/CustomFileInput.vue';
import { corporateI18n } from '@/i18n/mixins';
import websiteApi from '@/utils/Weflix/website';
import apiAWS from '@/utils/storage/aws';
import { canConfigureCorporate } from '@/utils/corporate/permissions';

const emptyForm = () => ({
    siteEnabled: true,
    showCertificate: true,
    showInquiry: false,
    showForum: false,
    url: '',
    url1: '',
    url2: '',
});

function normalizeUrlField(value) {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

function toOpenableUrl(value) {
    const raw = normalizeUrlField(value);
    if (!raw) return '';
    if (/^https?:\/\//i.test(raw)) return raw;
    return `https://${raw}`;
}

/** Website.status / flags: 0 = inativo, 1 = ativo */
function toBoolFlag(value, fallback = false) {
    if (value === undefined || value === null) return fallback;
    return value === 1 || value === true || value === '1';
}

function toApiFlag(value) {
    return value ? 1 : 0;
}

export default {
    name: 'CorporatePreferences',
    mixins: [corporateI18n],
    components: { TitlePage, Loader, CustomFileInput },
    data() {
        return {
            activeTab: 'site',
            loading: true,
            saving: false,
            savingKey: null,
            error: false,
            form: emptyForm(),
            websiteSnapshot: null,
            logoValue: '',
            logoPreview: null,
            logoUploadToken: null,
        };
    },
    computed: {
        ...mapState(['token', 'websites', 'corporateCompanySettings']),
        canEdit() {
            return canConfigureCorporate();
        },
        tabs() {
            return [
                { id: 'site', label: this.t.preferences.tabPortal },
                { id: 'collaborator', label: this.t.preferences.tabCollaborator },
                { id: 'banner', label: this.t.preferences.tabBanner },
            ];
        },
        collaboratorToggleItems() {
            return [
                {
                    key: 'showCertificate',
                    title: this.t.preferences.showCertificate,
                    hint: this.t.preferences.showCertificateHint,
                },
                {
                    key: 'showInquiry',
                    title: this.t.preferences.showInquiry,
                    hint: this.t.preferences.showInquiryHint,
                },
                {
                    key: 'showForum',
                    title: this.t.preferences.showForum,
                    hint: this.t.preferences.showForumHint,
                },
            ];
        },
        siteUrlItems() {
            return [
                {
                    key: 'url',
                    title: this.t.preferences.url,
                    hint: this.t.preferences.urlHint,
                },
                {
                    key: 'url1',
                    title: this.t.preferences.url1,
                    hint: this.t.preferences.url1Hint,
                },
                {
                    key: 'url2',
                    title: this.t.preferences.url2,
                    hint: this.t.preferences.url2Hint,
                },
            ];
        },
    },
    async created() {
        await this.load();
    },
    beforeUnmount() {
        this.revokeBlob(this.logoPreview);
    },
    methods: {
        ...mapActions(['storeCorporateCompanySettings', 'storeWebsites']),
        revokeBlob(url) {
            if (url && typeof url === 'string' && url.startsWith('blob:')) {
                URL.revokeObjectURL(url);
            }
        },
        syncLocalSettings(website) {
            const current = this.corporateCompanySettings || {};
            const siteEnabled = toBoolFlag(website?.status, true);
            const next = {
                ...current,
                isCorporate: true,
                siteEnabled,
                portal: {
                    ...(current.portal || {}),
                    showCertificate: toBoolFlag(website?.showCertificate, true),
                    showInquiry: toBoolFlag(website?.showInquiry, false),
                    showForum: toBoolFlag(website?.showForum, false),
                },
            };
            this.storeCorporateCompanySettings({ settings: next });
            return next;
        },
        applyMediaFromWebsite(website) {
            const logoUrl = typeof website?.logoFinal === 'string'
                ? website.logoFinal
                : (typeof website?.logo === 'string' && website.logo.startsWith('http')
                    ? website.logo
                    : '');
            this.logoValue = logoUrl;
            this.logoPreview = logoUrl || null;
            this.logoUploadToken = null;
        },
        async load() {
            this.loading = true;
            this.error = false;
            try {
                const website = await websiteApi.getWebsite(this.token);
                if (!website) {
                    this.error = true;
                    return;
                }

                this.websiteSnapshot = website;
                this.form = {
                    siteEnabled: toBoolFlag(website.status, true),
                    showCertificate: toBoolFlag(website.showCertificate, true),
                    showInquiry: toBoolFlag(website.showInquiry, false),
                    showForum: toBoolFlag(website.showForum, false),
                    url: normalizeUrlField(website.url),
                    url1: normalizeUrlField(website.url1),
                    url2: normalizeUrlField(website.url2),
                };
                this.applyMediaFromWebsite(website);

                this.syncLocalSettings(website);
                this.storeWebsites({ websites: website });
            } catch (e) {
                console.error('[corporate] falha ao carregar /website', e);
                this.error = true;
            } finally {
                this.loading = false;
            }
        },
        async putFileAws(url, file) {
            await apiAWS.putFileAWS(url, file);
        },
        async uploadImage(file) {
            const signature = await apiAWS.getSignatureImage(this.token, file.name);
            await this.putFileAws(signature.url, file);
            return signature.token;
        },
        async onLogoChange(value) {
            if (!this.canEdit) return;
            if (value && typeof value === 'object' && value instanceof File) {
                this.revokeBlob(this.logoPreview);
                this.logoPreview = URL.createObjectURL(value);
                try {
                    const localPreview = this.logoPreview;
                    this.logoUploadToken = await this.uploadImage(value);
                    const updated = await this.persistWebsitePatch({
                        logo: this.logoUploadToken,
                    });
                    this.applyMediaFromWebsite(updated);
                    if (!this.logoPreview && localPreview) {
                        this.logoValue = localPreview;
                        this.logoPreview = localPreview;
                    }
                    toast.success(this.t.preferences.saved);
                } catch (e) {
                    console.error('[corporate] falha no upload do logo', e);
                    toast.error(this.t.common.error);
                }
            } else if (typeof value === 'string') {
                this.logoValue = value;
                this.logoPreview = value || null;
            }
        },
        buildWebsitePayload(extra = {}) {
            return {
                ...(this.websiteSnapshot || {}),
                status: toApiFlag(this.form.siteEnabled),
                showCertificate: toApiFlag(this.form.showCertificate),
                showInquiry: toApiFlag(this.form.showInquiry),
                showForum: toApiFlag(this.form.showForum),
                url: normalizeUrlField(this.form.url) || null,
                url1: normalizeUrlField(this.form.url1) || null,
                url2: normalizeUrlField(this.form.url2) || null,
                ...extra,
            };
        },
        canOpenUrl(value) {
            return Boolean(toOpenableUrl(value));
        },
        openUrl(value) {
            const href = toOpenableUrl(value);
            if (!href) return;
            window.open(href, '_blank', 'noopener,noreferrer');
        },
        async copyUrl(value) {
            const href = toOpenableUrl(value);
            if (!href) return;
            try {
                await navigator.clipboard.writeText(href);
                toast.success(this.t.preferences.copied);
            } catch (e) {
                console.error('[corporate] falha ao copiar URL', e);
                toast.error(this.t.common.error);
            }
        },
        applyUrlsFromWebsite(website) {
            this.form = {
                ...this.form,
                url: normalizeUrlField(website?.url),
                url1: normalizeUrlField(website?.url1),
                url2: normalizeUrlField(website?.url2),
            };
        },
        async onUrlBlur(key) {
            if (!this.canEdit || this.saving || this.loading) return;
            const next = normalizeUrlField(this.form[key]);
            const previous = normalizeUrlField(this.websiteSnapshot?.[key]);
            if (next === previous) return;

            this.saving = true;
            this.savingKey = key;
            try {
                await this.persistWebsitePatch();
                this.applyUrlsFromWebsite(this.websiteSnapshot);
                toast.success(this.t.preferences.saved);
            } catch (e) {
                console.error('[corporate] falha ao salvar URL', e);
                this.form = {
                    ...this.form,
                    [key]: previous,
                };
                toast.error(this.t.common.error);
            } finally {
                this.saving = false;
                this.savingKey = null;
            }
        },
        async persistWebsitePatch(extra = {}) {
            if (!this.websiteSnapshot) {
                this.websiteSnapshot = await websiteApi.getWebsite(this.token);
            }
            const websitePayload = this.buildWebsitePayload(extra);
            const updatedWebsite = await websiteApi.editWebsite(
                this.token,
                this.websiteSnapshot?.id,
                websitePayload,
            );
            this.websiteSnapshot = updatedWebsite || websitePayload;
            this.storeWebsites({ websites: this.websiteSnapshot });
            this.syncLocalSettings(this.websiteSnapshot);
            try {
                await websiteApi.refreshCache(this.token);
            } catch (cacheError) {
                console.warn('[corporate] refreshCache falhou (não bloqueia save)', cacheError);
            }
            return this.websiteSnapshot;
        },
        async onToggle(key, value) {
            if (!this.canEdit || this.saving || this.loading) return;

            const previous = this.form[key];
            this.form = {
                ...this.form,
                [key]: Boolean(value),
            };

            this.saving = true;
            this.savingKey = key;
            try {
                await this.persistWebsitePatch();
                toast.success(this.t.preferences.saved);
            } catch (e) {
                console.error('[corporate] falha ao salvar configurações', e);
                this.form = {
                    ...this.form,
                    [key]: previous,
                };
                toast.error(this.t.common.error);
            } finally {
                this.saving = false;
                this.savingKey = null;
            }
        },
    },
};
</script>

<style scoped>
@import '@/css/weeduc/admin/corporate.css';

.corporate-preferences-panel {
    text-align: left;
}

.corporate-preferences-lead {
    margin: 0 0 1rem;
    color: #5c6370;
    font-size: 0.875rem;
    line-height: 1.45;
}

.corporate-preferences-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
}

.corporate-preferences-row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    align-items: center;
    padding: 1rem 1.1rem;
    border: 1px solid #f0f2f5;
    border-radius: 12px;
    background: #fafbfc;
}

.corporate-preferences-row strong {
    display: block;
    color: #1a1d26;
    font-size: 0.95rem;
}

.corporate-preferences-row p {
    margin: 0.25rem 0 0;
    color: #5c6370;
    font-size: 0.85rem;
    line-height: 1.45;
}

.corporate-preferences-upload {
    max-width: 360px;
}

.corporate-preferences-urls {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 0.75rem;
}

.corporate-preferences-url-card {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    min-width: 0;
    padding: 1rem 1.1rem;
    border: 1px solid #f0f2f5;
    border-radius: 12px;
    background: #fafbfc;
}

.corporate-preferences-url-card--main {
    grid-column: 1 / -1;
}

.corporate-preferences-url-card.is-saving {
    opacity: 0.72;
}

.corporate-preferences-url-card__copy strong {
    display: block;
    color: #1a1d26;
    font-size: 0.95rem;
}

.corporate-preferences-url-card__copy p {
    margin: 0.25rem 0 0;
    color: #5c6370;
    font-size: 0.85rem;
    line-height: 1.45;
}

.corporate-preferences-url-card__control {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
}

.corporate-preferences-url-card__input {
    flex: 1;
    min-width: 0;
    height: 42px;
    padding: 0 0.9rem;
    border: 1px solid #e4e7ee;
    border-radius: 10px;
    background: #fff;
    color: #1a1d26;
    font: inherit;
    font-size: 0.9rem;
    outline: none;
}

.corporate-preferences-url-card__input:focus {
    border-color: #160266;
    box-shadow: 0 0 0 3px rgba(22, 2, 102, 0.08);
}

.corporate-preferences-url-card__input:disabled {
    color: #8b90a0;
    background: #f3f4f7;
}

.corporate-preferences-url-card__actions {
    display: flex;
    flex-shrink: 0;
    gap: 0.15rem;
}

.corporate-preferences-url-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    border: 1px solid #e4e7ee;
    border-radius: 10px;
    background: #fff;
    color: #5c6370;
    cursor: pointer;
}

.corporate-preferences-url-action:hover:not(:disabled) {
    border-color: #d8dbe3;
    background: #f4f5f8;
    color: #160266;
}

.corporate-preferences-url-action:disabled {
    opacity: 0.4;
    cursor: default;
}

.corporate-preferences-banner {
    padding: 0.9rem 1rem;
    margin-bottom: 1rem;
    border-radius: 12px;
    background: #eef0fc;
    color: #160266;
    font-size: 0.875rem;
    font-weight: 600;
}

.corporate-preferences-page .corporate-section-label {
    margin: 1.75rem 0 0.65rem;
}

.corporate-preferences-page .corporate-section-label:first-of-type {
    margin-top: 0;
}

@media (max-width: 720px) {
    .corporate-preferences-row {
        flex-direction: column;
        align-items: flex-start;
    }

    .corporate-preferences-urls {
        grid-template-columns: 1fr;
    }

    .corporate-preferences-url-card__control {
        flex-wrap: wrap;
    }

    .corporate-preferences-url-card__input {
        width: 100%;
        flex: 1 1 100%;
    }
}

.dashboard.dark .corporate-preferences-row,
.dashboard.dark .corporate-preferences-url-card {
    background: #252730;
    border-color: #353945;
}

.dashboard.dark .corporate-preferences-row strong,
.dashboard.dark .corporate-preferences-url-card__copy strong {
    color: #f1f3f7;
}

.dashboard.dark .corporate-preferences-row p,
.dashboard.dark .corporate-preferences-lead,
.dashboard.dark .corporate-preferences-url-card__copy p {
    color: #9ca3b4;
}

.dashboard.dark .corporate-preferences-banner {
    background: rgba(165, 180, 252, 0.12);
    color: #c4b5fd;
}

.dashboard.dark .corporate-section-label {
    color: rgba(255, 255, 255, 0.45);
}

.dashboard.dark .corporate-preferences-url-card__input {
    background: #1f222b;
    border-color: #353945;
    color: #f1f3f7;
}

.dashboard.dark .corporate-preferences-url-card__input:focus {
    border-color: #c4b5fd;
    box-shadow: 0 0 0 3px rgba(165, 180, 252, 0.16);
}

.dashboard.dark .corporate-preferences-url-card__input:disabled {
    background: #2a2e3a;
    color: #9ca3b4;
}

.dashboard.dark .corporate-preferences-url-action {
    background: #1f222b;
    border-color: #353945;
    color: #9ca3b4;
}

.dashboard.dark .corporate-preferences-url-action:hover:not(:disabled) {
    background: rgba(165, 180, 252, 0.12);
    border-color: #454a5a;
    color: #c4b5fd;
}
</style>
